import axios from 'axios';
import { StorageManager } from '../utils/storageManager';
import { getErrorMessage } from '../utils/errorMessages';
import { NON_AGENCY_SINGLE_SEGMENT_ROUTES } from '../constants/seoRoutes';

// In development, use the Vite proxy (relative URL)
// In production, use the full API URL from environment variable
const baseURL = import.meta.env.PROD 
  ? import.meta.env.VITE_API_URL || 'https://api.landflow.com/api'  // Production API URL
  : import.meta.env.VITE_API_URL || 'http://localhost:3000/api';  // Development API URL

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for CORS with credentials
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Determine which token to use based on the endpoint
    const url = config.url || '';
    // Contractor endpoints (only specific contractor-only endpoints)
    const isContractorEndpoint = (
      url.startsWith('/contractor-auth') ||
      url.startsWith('/contractor-availability/my-availability') || // Contractor's own availability
      url.startsWith('/contractors') ||
      url.startsWith('/jobs') // include job quote/respond, details, etc.
    );
    // Agency endpoints (including contractor job photos under agency path)
    const isAgencyEndpoint = url.startsWith('/agencies/');

    let token;
    if (isAgencyEndpoint) {
      token = StorageManager.getToken();
    } else if (isContractorEndpoint) {
      token = localStorage.getItem('contractor_token');
      console.log('🔑 API request to contractor endpoint:', url);
      console.log('🔑 Using contractor token:', !!token);
    } else {
      // Default to agent token (for agent endpoints like /contractor-availability/contractors)
      token = StorageManager.getToken();
    }
    
    if (token) {
      // Ensure headers object exists (axios should create it, but be safe)
      if (!config.headers) {
        config.headers = {};
      }
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Log warning if no token for agency endpoints (except public endpoints like /agencies/:slug/public)
      if (isAgencyEndpoint && !url.includes('/public')) {
        console.warn('⚠️ API request to agency endpoint without token:', url);
      }
    }
    return config;
  },
  (error) => {
    console.error('API request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Handle 304 Not Modified responses by returning the cached response.
    // Retrying with cache-busting was causing repeated request loops.
    if (response.status === 304) {
      return response;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      const currentPath = window.location.pathname;
      const isContractorRoute = currentPath.startsWith('/contractor') || currentPath.startsWith('/jobs');
      
      // Only clear the appropriate token
      if (isContractorRoute) {
        localStorage.removeItem('contractor_token');
        localStorage.removeItem('contractor_info');
      } else {
        StorageManager.removeToken();
      }
      
      // Check the current path to determine redirect
      const isAdminRoute = currentPath.startsWith('/admin');
      const isAgencyRoute = currentPath.startsWith('/agencies/');
      
      // Agency login page at /:agencySlug (e.g. /demo-properties) - single segment, not a reserved route
      const pathParts = currentPath.split('/').filter(Boolean);
      const isAgencyLoginPage = pathParts.length === 1 && !NON_AGENCY_SINGLE_SEGMENT_ROUTES.has(pathParts[0]);
      
      if (isAdminRoute) {
        window.location.href = '/admin/login';
      } else if (isAgencyRoute) {
        // Extract agency slug from path like /agencies/tracy-yap-realty/...
        const agencySlug = pathParts[1]; // pathParts = ['agencies', slug, ...]
        window.location.href = `/${agencySlug}`;
      } else if (isAgencyLoginPage) {
        // Already on agency login page (e.g. /demo-properties) - stay put, just clear token; component will show form
        // Don't redirect - user is where they need to be
      } else if (isContractorRoute) {
        window.location.href = '/contractor/auth';
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api; 